import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import {
  getBanks,
  deleteBank,
  toggleBankStatus,
  clearError,
  clearSuccess,
} from '../../../../redux/Slices/authSlice';
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaToggleOn,
  FaToggleOff,
  FaSearch,
  FaPrint,
  FaFileExcel,
  FaSort,
  FaSortUp,
  FaSortDown,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { matchesActiveFilter } from '../../../../shared/utils.js/utils';
import usePdfExport from '../../../../shared/pdf/usePdfExport';
import PdfExportManager from '../../../../shared/pdf/PdfExportManager';
import { exportRowsToExcel } from '../../../../shared/utils.js/excelExport';

const SORT_FIELDS = {
  INDEX: '__index',
  NAME: 'name_ar',
  ACCOUNT: 'account_number',
  IBAN: 'iban',
  SWIFT: 'swift_code',
  STATUS: 'is_active',
};

const Banks = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { setTitle } = useTitle();
  const { banksPagination, isLoading, error, success } = useSelector(
    state => state.auth,
  );
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [isActiveFilter, setIsActiveFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [bankToDelete, setBankToDelete] = useState(null);
  const [allBanks, setAllBanks] = useState([]);
  const [perPage, setPerPage] = useState(10);
  const [perPageInput, setPerPageInput] = useState(10);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const lastSuccessRef = useRef({ message: null, time: 0 });
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState('asc');
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';

  useEffect(() => {
    setTitle('sidenav.banks');
  }, [setTitle]);

  // Fetch the full list once (filtering/search/sort are client-side).
  const fetchBanks = useCallback(async () => {
    try {
      const result = await dispatch(getBanks({ page: 1, per_page: 10 }));
      if (!getBanks.fulfilled.match(result)) return;
      const response = result.payload;
      const data = response?.data || response;
      const paginationData = data?.meta || data?.pagination || null;
      const firstPageList = Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data)
          ? data
          : [];
      const total = paginationData?.total || firstPageList.length;

      if (total <= firstPageList.length) {
        setAllBanks(firstPageList);
        return;
      }
      const allResult = await dispatch(getBanks({ page: 1, per_page: total }));
      if (getBanks.fulfilled.match(allResult)) {
        const allData = allResult.payload?.data || allResult.payload;
        setAllBanks(
          Array.isArray(allData?.data)
            ? allData.data
            : Array.isArray(allData)
              ? allData
              : [],
        );
      }
    } catch {
      /* errors surface via the auth slice */
    }
  }, [dispatch]);

  useEffect(() => {
    fetchBanks();
  }, [fetchBanks, i18n.language]);

  useEffect(() => {
    dispatch(clearSuccess());
    dispatch(clearError());
  }, [dispatch]);

  useEffect(() => {
    if (success) {
      const now = Date.now();
      const last = lastSuccessRef.current;
      if (!last.message || last.message !== success || now - last.time > 2000) {
        toast.success(success, { rtl: isRTL, autoClose: 3000 });
        lastSuccessRef.current = { message: success, time: now };
      }
      dispatch(clearSuccess());
      fetchBanks();
    }
  }, [success, dispatch, isRTL, fetchBanks]);

  useEffect(() => {
    if (error) {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (!last.message || last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL, autoClose: 5000 });
        lastErrorRef.current = { message: error, time: now };
      }
      setTimeout(() => dispatch(clearError()), 3000);
    }
  }, [error, dispatch, isRTL]);

  const handleSort = field => {
    if (sortField === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = field => {
    if (sortField !== field)
      return <FaSort style={{ opacity: 0.4, marginInlineStart: '4px' }} />;
    return sortOrder === 'asc' ? (
      <FaSortUp style={{ marginInlineStart: '4px' }} />
    ) : (
      <FaSortDown style={{ marginInlineStart: '4px' }} />
    );
  };

  const filteredBanks = useMemo(() => {
    let result = (allBanks || []).filter(bank => {
      if (!matchesActiveFilter(bank.is_active, isActiveFilter)) return false;
      if (!searchTerm) return true;
      const q = searchTerm.trim().toLowerCase();
      return (
        (bank.name_ar || '').toLowerCase().includes(q) ||
        (bank.name_en || '').toLowerCase().includes(q) ||
        (bank.account_number || '').toLowerCase().includes(q) ||
        (bank.iban || '').toLowerCase().includes(q) ||
        (bank.swift_code || '').toLowerCase().includes(q)
      );
    });
    if (sortField === SORT_FIELDS.INDEX) {
      return sortOrder === 'asc' ? result : [...result].reverse();
    }
    if (sortField) {
      result = [...result].sort((a, b) => {
        let aVal = a[sortField] ?? '';
        let bVal = b[sortField] ?? '';
        if (typeof aVal === 'boolean') aVal = aVal ? 1 : 0;
        if (typeof bVal === 'boolean') bVal = bVal ? 1 : 0;
        const cmp = String(aVal).localeCompare(String(bVal), undefined, {
          numeric: true,
        });
        return sortOrder === 'asc' ? cmp : -cmp;
      });
    }
    return result;
  }, [allBanks, searchTerm, isActiveFilter, sortField, sortOrder]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredBanks.length / Math.max(1, perPage)),
  );
  const currentPageSafe = Math.min(currentPage, totalPages);
  const paginatedBanks = filteredBanks.slice(
    (currentPageSafe - 1) * perPage,
    currentPageSafe * perPage,
  );

  const handlePerPageInputChange = value => {
    const maxAllowed = filteredBanks.length > 0 ? filteredBanks.length : 1000;
    const numericValue = Number(value);
    const safeValue = Number.isNaN(numericValue)
      ? 1
      : Math.min(Math.max(1, Math.floor(numericValue)), maxAllowed);
    setPerPageInput(safeValue);
  };

  const handleApplyPerPage = () => {
    const maxAllowed = filteredBanks.length > 0 ? filteredBanks.length : 1000;
    const nextValue = Math.min(Math.max(1, Number(perPageInput) || 1), maxAllowed);
    setPerPage(nextValue);
    setPerPageInput(nextValue);
    setCurrentPage(1);
  };

  const handleSearchChange = e => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleFilterChange = e => {
    setIsActiveFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleDelete = bank => {
    setBankToDelete(bank);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (bankToDelete) {
      await dispatch(deleteBank(bankToDelete.id));
      setShowDeleteModal(false);
      setBankToDelete(null);
      fetchBanks();
    }
  };

  const handleToggleStatus = id => {
    dispatch(toggleBankStatus(id));
  };

  // ---- Shared PDF / Excel column config -------------------------------
  const bankName = bank =>
    (isRTL ? bank.name_ar : bank.name_en) ||
    bank.name_ar ||
    bank.name_en ||
    '—';
  const bankStatus = bank =>
    bank.is_active
      ? t('banks.status.active', { defaultValue: 'Active' })
      : t('banks.status.inactive', { defaultValue: 'Inactive' });

  const exportColumns = useMemo(
    () => [
      {
        key: 'name',
        label: t('banks.table.name', { defaultValue: 'Name' }),
        align: 'start',
        render: bankName,
        excelValue: bankName,
      },
      {
        key: 'account_number',
        label: t('banks.table.accountNumber', { defaultValue: 'Account Number' }),
      },
      { key: 'iban', label: t('banks.table.iban', { defaultValue: 'IBAN' }) },
      {
        key: 'swift_code',
        label: t('banks.table.swiftCode', { defaultValue: 'SWIFT Code' }),
      },
      {
        key: 'is_active',
        label: t('banks.table.status', { defaultValue: 'Status' }),
        render: bankStatus,
        excelValue: bankStatus,
      },
      {
        key: 'notes',
        label: t('banks.table.notes', { defaultValue: 'Notes' }),
        align: 'start',
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, isRTL],
  );

  const pdf = usePdfExport({
    idPrefix: 'banks',
    isRTL,
    fileNamePrefix: 'ACE_Banks',
    onError: err =>
      toast.error(
        err?.message === 'NO_DATA'
          ? t('banks.print.noData', { defaultValue: 'No data available for printing' })
          : t('banks.print.error', { defaultValue: 'An error occurred while creating the PDF' }),
        { rtl: isRTL },
      ),
  });

  const handleExportBankToExcel = bank => {
    exportRowsToExcel({
      columns: exportColumns,
      rows: [bank],
      fileName: `${t('banks.excel.fileName', { defaultValue: 'Bank' })}_${bankName(bank)}`,
      sheetName: t('banks.excel.sheetName', { defaultValue: 'Bank' }),
      rtl: isRTL,
    });
    toast.success(
      t('banks.export.success', { defaultValue: 'Data exported successfully' }),
      { rtl: isRTL },
    );
  };

  const handleExportAllToExcel = () => {
    if (!filteredBanks || filteredBanks.length === 0) {
      toast.error(
        t('banks.export.noData', { defaultValue: 'No data to export' }),
        { rtl: isRTL },
      );
      return;
    }
    exportRowsToExcel({
      columns: exportColumns,
      rows: filteredBanks,
      fileName: t('banks.excel.fileName', { defaultValue: 'Banks' }),
      sheetName: t('banks.excel.sheetName', { defaultValue: 'Banks' }),
      rtl: isRTL,
    });
    toast.success(
      t('banks.export.success', { defaultValue: 'Data exported successfully' }),
      { rtl: isRTL },
    );
  };

  // Single-record PDF content — key/value grid (same layout as before)
  const renderSingleBank = bank => (
    <div style={{ border: '1px solid #000', padding: '12px', marginBottom: '12px' }}>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '10px',
          fontSize: '13px',
          lineHeight: 1.6,
          direction: dir,
          textAlign,
        }}
      >
        <div>
          <strong>{t('banks.print.name', { defaultValue: 'Name' })}:</strong>{' '}
          {bankName(bank)}
        </div>
        <div>
          <strong>
            {t('banks.print.accountNumber', { defaultValue: 'Account Number' })}:
          </strong>{' '}
          {bank.account_number || '—'}
        </div>
        <div>
          <strong>{t('banks.print.iban', { defaultValue: 'IBAN' })}:</strong>{' '}
          {bank.iban || '—'}
        </div>
        <div>
          <strong>
            {t('banks.print.swiftCode', { defaultValue: 'SWIFT Code' })}:
          </strong>{' '}
          {bank.swift_code || '—'}
        </div>
        <div>
          <strong>{t('banks.print.status', { defaultValue: 'Status' })}:</strong>{' '}
          {bankStatus(bank)}
        </div>
        <div style={{ gridColumn: '1 / -1' }}>
          <strong>{t('banks.print.notes', { defaultValue: 'Notes' })}:</strong>{' '}
          {bank.notes || '—'}
        </div>
      </div>
    </div>
  );

  if (isLoading && (!allBanks || allBanks.length === 0)) {
    return (
      <div
        style={{
          padding: '30px',
          backgroundColor: '#14263b',
          minHeight: 'calc(100vh - 80px)',
          color: 'white',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          direction: dir,
        }}
      >
        {t('common.loading', { defaultValue: 'Loading...' })}
      </div>
    );
  }

  const noData = !filteredBanks || filteredBanks.length === 0;

  return (
    <>
      <div
        style={{
          padding: '30px',
          backgroundColor: '#14263b',
          minHeight: 'calc(100vh - 80px)',
          color: 'white',
          direction: dir,
        }}
      >
        <div style={{ marginBottom: '30px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '20px',
              flexWrap: 'wrap',
              gap: '15px',
            }}
          >
            <h1
              style={{
                fontSize: '24px',
                fontWeight: 'bold',
                color: 'white',
                margin: 0,
                textAlign,
              }}
            >
              {t('banks.title', { defaultValue: 'Banks' })}
            </h1>

            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={() => pdf.previewAll(filteredBanks)}
                disabled={noData}
                style={{
                  backgroundColor: noData ? '#666' : '#444',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: noData ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <FaEye />{' '}
                {t('banks.preview.all', { defaultValue: 'Preview PDF All' })}
              </button>
              <button
                onClick={() => pdf.printAll(filteredBanks)}
                disabled={pdf.isGeneratingPdf || noData}
                style={{
                  backgroundColor:
                    pdf.isGeneratingPdf || noData ? '#666' : '#1f5ff1',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: pdf.isGeneratingPdf || noData ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <FaPrint />
                {t('banks.print.all', { defaultValue: 'Print PDF All' })}
              </button>
              <button
                onClick={handleExportAllToExcel}
                disabled={noData}
                style={{
                  backgroundColor: noData ? '#666' : '#27ae60',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  cursor: noData ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <FaFileExcel />
                {t('banks.export.all', { defaultValue: 'Export Excel' })}
              </button>
              <button
                onClick={() => navigate('/banks/add')}
                style={{
                  backgroundColor: '#AC2000',
                  color: 'white',
                  border: 'none',
                  padding: '12px 20px',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <FaPlus /> {t('banks.add', { defaultValue: 'Add Bank' })}
              </button>
            </div>
          </div>

          {/* Filters */}
          <div
            style={{
              display: 'flex',
              gap: '15px',
              marginBottom: '20px',
              flexWrap: 'wrap',
              alignItems: 'center',
            }}
          >
            <div
              style={{
                position: 'relative',
                flex: '1',
                maxWidth: '800px',
                minWidth: '300px',
              }}
            >
              <FaSearch
                style={{
                  position: 'absolute',
                  [isRTL ? 'left' : 'right']: '15px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: '#666',
                  fontSize: '16px',
                  zIndex: 1,
                  pointerEvents: 'none',
                }}
              />
              <input
                type="text"
                placeholder={t('banks.searchPlaceholder', {
                  defaultValue: 'Search by name, account number, IBAN...',
                })}
                value={searchTerm}
                onChange={handleSearchChange}
                aria-label={t('banks.searchPlaceholder', {
                  defaultValue: 'Search',
                })}
                style={{
                  width: '100%',
                  padding: isRTL ? '12px 45px 12px 15px' : '12px 15px 12px 45px',
                  backgroundColor: '#1a1f2e',
                  border: '1px solid #333',
                  borderRadius: '8px',
                  color: 'white',
                  fontSize: '14px',
                  direction: dir,
                  textAlign,
                  boxSizing: 'border-box',
                }}
              />
            </div>
            <select
              value={isActiveFilter}
              onChange={handleFilterChange}
              aria-label={t('banks.filter.allStatus', { defaultValue: 'Status filter' })}
              style={{
                padding: '12px 15px',
                backgroundColor: '#1b3350',
                border: '1px solid #333',
                borderRadius: '8px',
                color: 'white',
                fontSize: '14px',
                minWidth: '150px',
                cursor: 'pointer',
                direction: dir,
                textAlign,
              }}
            >
              <option value="">
                {t('banks.filter.allStatus', { defaultValue: 'All Status' })}
              </option>
              <option value="1">
                {t('banks.status.active', { defaultValue: 'Active' })}
              </option>
              <option value="0">
                {t('banks.status.inactive', { defaultValue: 'Inactive' })}
              </option>
            </select>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                flexWrap: 'wrap',
              }}
            >
              <label
                style={{ color: 'white', fontSize: '14px', whiteSpace: 'nowrap' }}
              >
                {t('common.perPage', { defaultValue: 'عدد السجلات' })}:
              </label>
              <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                <button
                  onClick={() => handlePerPageInputChange(perPageInput - 1)}
                  style={{
                    backgroundColor: '#1b3350',
                    color: 'white',
                    border: '1px solid #333',
                    padding: '6px 10px',
                    borderRadius: '6px 0 0 6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    minWidth: '32px',
                  }}
                >
                  −
                </button>
                <input
                  type="number"
                  min="1"
                  max={filteredBanks?.length || 1000}
                  value={perPageInput}
                  onChange={e => handlePerPageInputChange(e.target.value)}
                  style={{
                    width: '70px',
                    padding: '6px 10px',
                    backgroundColor: '#1a1f2e',
                    border: '1px solid #333',
                    borderLeft: 'none',
                    borderRight: 'none',
                    borderRadius: 0,
                    color: 'white',
                    fontSize: '14px',
                    textAlign: 'center',
                  }}
                />
                <button
                  onClick={() => handlePerPageInputChange(perPageInput + 1)}
                  style={{
                    backgroundColor: '#1b3350',
                    color: 'white',
                    border: '1px solid #333',
                    padding: '6px 10px',
                    borderRadius: '0 6px 6px 0',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold',
                    minWidth: '32px',
                  }}
                >
                  +
                </button>
              </div>
              <button
                onClick={handleApplyPerPage}
                style={{
                  backgroundColor: '#AC2000',
                  color: 'white',
                  border: 'none',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                }}
              >
                {t('common.apply', { defaultValue: 'تطبيق' })}
              </button>
            </div>
          </div>
        </div>

        {/* Table */}
        <div
          style={{
            backgroundColor: '#1b3350',
            borderRadius: '12px',
            border: '1px solid #333',
            overflow: 'hidden',
          }}
        >
          {paginatedBanks && paginatedBanks.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr
                    style={{
                      backgroundColor: '#1a1f2e',
                      borderBottom: '2px solid #AC2000',
                    }}
                  >
                    <th
                      onClick={() => handleSort(SORT_FIELDS.INDEX)}
                      style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                    >
                      {t('common.serialNumber', { defaultValue: '#' })}
                      {getSortIcon(SORT_FIELDS.INDEX)}
                    </th>
                    <th
                      onClick={() => handleSort(SORT_FIELDS.NAME)}
                      style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                    >
                      {t('banks.table.name', { defaultValue: 'Name' })}
                      {getSortIcon(SORT_FIELDS.NAME)}
                    </th>
                    <th
                      onClick={() => handleSort(SORT_FIELDS.ACCOUNT)}
                      style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                    >
                      {t('banks.table.accountNumber', {
                        defaultValue: 'Account Number',
                      })}
                      {getSortIcon(SORT_FIELDS.ACCOUNT)}
                    </th>
                    <th
                      onClick={() => handleSort(SORT_FIELDS.IBAN)}
                      style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                    >
                      {t('banks.table.iban', { defaultValue: 'IBAN' })}
                      {getSortIcon(SORT_FIELDS.IBAN)}
                    </th>
                    <th
                      onClick={() => handleSort(SORT_FIELDS.SWIFT)}
                      style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                    >
                      {t('banks.table.swiftCode', { defaultValue: 'SWIFT Code' })}
                      {getSortIcon(SORT_FIELDS.SWIFT)}
                    </th>
                    <th
                      onClick={() => handleSort(SORT_FIELDS.STATUS)}
                      style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }}
                    >
                      {t('banks.table.status', { defaultValue: 'Status' })}
                      {getSortIcon(SORT_FIELDS.STATUS)}
                    </th>
                    <th style={thStyle}>
                      {t('banks.table.notes', { defaultValue: 'Notes' })}
                    </th>
                    <th style={thStyle}>
                      {t('banks.table.actions', { defaultValue: 'Actions' })}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedBanks.map((bank, index) => {
                    const recordIndex =
                      (currentPageSafe - 1) * perPage + index + 1;
                    return (
                      <tr
                        key={bank.id}
                        style={{
                          borderBottom: '2px solid #444',
                          borderTop: index === 0 ? '2px solid #444' : 'none',
                        }}
                      >
                        <td style={tdCenter}>{recordIndex}</td>
                        <td
                          style={{
                            padding: '15px',
                            textAlign,
                            color: 'white',
                            direction: dir,
                            verticalAlign: 'middle',
                          }}
                        >
                          {bankName(bank)}
                        </td>
                        <td style={tdCenter}>
                          {bank.account_number ||
                            t('banks.notAvailable', { defaultValue: '—' })}
                        </td>
                        <td style={tdCenter}>
                          {bank.iban ||
                            t('banks.notAvailable', { defaultValue: '—' })}
                        </td>
                        <td style={tdCenter}>
                          {bank.swift_code ||
                            t('banks.notAvailable', { defaultValue: '—' })}
                        </td>
                        <td style={{ ...tdCenter, color: undefined }}>
                          <span
                            style={{
                              padding: '5px 15px',
                              borderRadius: '20px',
                              backgroundColor: bank.is_active
                                ? 'rgba(172, 32, 0, 0.4)'
                                : 'rgba(220, 53, 69, 0.4)',
                              color: 'white',
                              fontSize: '12px',
                              fontWeight: 'bold',
                              whiteSpace: 'nowrap',
                              display: 'inline-block',
                            }}
                          >
                            {bankStatus(bank)}
                          </span>
                        </td>
                        <td
                          style={{
                            padding: '15px',
                            textAlign,
                            color: 'white',
                            direction: dir,
                            maxWidth: '300px',
                            verticalAlign: 'middle',
                          }}
                        >
                          {bank.notes ||
                            t('banks.notAvailable', { defaultValue: '—' })}
                        </td>
                        <td style={{ padding: '15px' }}>
                          <div
                            style={{
                              display: 'flex',
                              gap: '8px',
                              justifyContent: 'center',
                            }}
                          >
                            <IconButton
                              bg="#666"
                              onClick={() => pdf.previewSingle(bank)}
                              title={t('banks.actions.view', {
                                defaultValue: 'View Details',
                              })}
                            >
                              <FaEye />
                            </IconButton>
                            <IconButton
                              bg="#1f5ff1"
                              onClick={() =>
                                pdf.printSingle(bank, bankName(bank))
                              }
                              disabled={pdf.isGeneratingPdf}
                              title={t('banks.actions.print', {
                                defaultValue: 'Print',
                              })}
                            >
                              <FaPrint />
                            </IconButton>
                            <IconButton
                              bg="#28a745"
                              onClick={() => handleExportBankToExcel(bank)}
                              title={t('banks.actions.export', {
                                defaultValue: 'Export Excel',
                              })}
                            >
                              <FaFileExcel />
                            </IconButton>
                            <IconButton
                              bg="#17a2b8"
                              onClick={() => navigate(`/banks/edit/${bank.id}`)}
                              title={t('banks.actions.edit', {
                                defaultValue: 'Edit',
                              })}
                            >
                              <FaEdit />
                            </IconButton>
                            <IconButton
                              bg="#c0392b"
                              onClick={() => handleDelete(bank)}
                              title={t('banks.actions.delete', {
                                defaultValue: 'Delete',
                              })}
                            >
                              <FaTrash />
                            </IconButton>
                            <IconButton
                              bg="#34495e"
                              onClick={() => handleToggleStatus(bank.id)}
                              title={t('banks.actions.toggleStatus', {
                                defaultValue: 'Toggle Status',
                              })}
                            >
                              {bank.is_active ? <FaToggleOn /> : <FaToggleOff />}
                            </IconButton>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
              {t('banks.noData', { defaultValue: 'No banks found' })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 &&
            (() => {
              const pages = [];
              for (let i = 1; i <= totalPages; i += 1) {
                if (
                  i === 1 ||
                  i === totalPages ||
                  (i >= currentPageSafe - 2 && i <= currentPageSafe + 2)
                ) {
                  pages.push(i);
                } else if (
                  i === currentPageSafe - 3 ||
                  i === currentPageSafe + 3
                ) {
                  pages.push('...');
                }
              }
              return (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    gap: '8px',
                    marginTop: '20px',
                    paddingBottom: '20px',
                    borderTop: '1px solid #333',
                    paddingTop: '20px',
                  }}
                >
                  <button
                    onClick={() => setCurrentPage(Math.max(1, currentPageSafe - 1))}
                    disabled={currentPageSafe === 1}
                    style={{
                      padding: '8px 12px',
                      backgroundColor:
                        currentPageSafe === 1 ? '#333' : '#AC2000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: currentPageSafe === 1 ? 'not-allowed' : 'pointer',
                    }}
                  >
                    {t('common.previous', { defaultValue: 'Previous' })}
                  </button>
                  {pages.map((p, idx) =>
                    p === '...' ? (
                      <span
                        key={`ellipsis-${idx}`}
                        style={{ padding: '8px', color: 'white' }}
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setCurrentPage(p)}
                        style={{
                          padding: '8px 12px',
                          backgroundColor:
                            p === currentPageSafe ? '#AC2000' : '#1a1f2e',
                          color: 'white',
                          border: '1px solid #333',
                          borderRadius: '4px',
                          cursor: 'pointer',
                        }}
                      >
                        {p}
                      </button>
                    ),
                  )}
                  <button
                    onClick={() =>
                      setCurrentPage(Math.min(totalPages, currentPageSafe + 1))
                    }
                    disabled={currentPageSafe === totalPages}
                    style={{
                      padding: '8px 12px',
                      backgroundColor:
                        currentPageSafe === totalPages ? '#333' : '#AC2000',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor:
                        currentPageSafe === totalPages
                          ? 'not-allowed'
                          : 'pointer',
                    }}
                  >
                    {t('common.next', { defaultValue: 'Next' })}
                  </button>
                </div>
              );
            })()}
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div
            style={{
              position: 'fixed',
              inset: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 999999,
            }}
          >
            <div
              style={{
                backgroundColor: '#1a1f2e',
                padding: '30px',
                borderRadius: '8px',
                width: '90%',
                maxWidth: '400px',
                border: '1px solid #333',
                direction: dir,
              }}
            >
              <h2
                style={{
                  color: 'white',
                  marginBottom: '20px',
                  fontSize: '20px',
                  textAlign,
                }}
              >
                {t('banks.delete.title', { defaultValue: 'Confirm Delete' })}
              </h2>
              <p style={{ color: '#ccc', marginBottom: '30px', textAlign }}>
                {t('banks.delete.confirm', {
                  defaultValue: 'Are you sure you want to delete the bank',
                })}{' '}
                "{bankToDelete ? bankName(bankToDelete) : ''}"?
              </p>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '12px',
                  flexDirection: isRTL ? 'row' : 'row-reverse',
                }}
              >
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setBankToDelete(null);
                  }}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#333',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  {t('banks.delete.cancel', { defaultValue: 'Cancel' })}
                </button>
                <button
                  onClick={confirmDelete}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                  }}
                >
                  {t('banks.delete.confirmButton', { defaultValue: 'Delete' })}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Shared PDF Engine — measurement, preview, print & single-record */}
      <PdfExportManager
        pdf={pdf}
        title={t('banks.title', { defaultValue: 'Banks' })}
        columns={exportColumns}
        rows={filteredBanks}
        singleTitle={t('banks.print.title', { defaultValue: 'Bank Details' })}
        renderSingle={renderSingleBank}
      />
    </>
  );
};

const thStyle = {
  padding: '15px',
  textAlign: 'center',
  color: 'white',
  fontWeight: 'bold',
  fontSize: '16px',
  borderBottom: '1px solid #333',
};

const tdCenter = {
  padding: '15px',
  textAlign: 'center',
  color: 'white',
  verticalAlign: 'middle',
};

const IconButton = ({ children, onClick, title, bg, disabled = false }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    title={title}
    aria-label={title}
    style={{
      backgroundColor: bg,
      color: 'white',
      border: 'none',
      padding: '8px',
      borderRadius: '6px',
      cursor: disabled ? 'not-allowed' : 'pointer',
      fontSize: '14px',
      opacity: disabled ? 0.7 : 1,
    }}
  >
    {children}
  </button>
);

export default Banks;
